import { useState, useEffect } from 'react'
import {
  Container, Text, Box, Paper, Group, Stack,
  Button, Loader, Badge, Progress, Alert,
} from '@mantine/core'
import { CheckCircle, XCircle, Trophy, RotateCcw, ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { vocabularyApi, VocabTestWord } from '../../api/vocabulary'

function renderSentence(sentence: string, targetWord: string) {
  const regex = new RegExp(`(\\b${targetWord}\\b)`, 'gi')
  const parts = sentence.split(regex)
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === targetWord.toLowerCase()
          ? <span key={i} style={{ textDecoration: 'underline', fontWeight: 700 }}>{part}</span>
          : <span key={i}>{part}</span>
      )}
    </>
  )
}

export function VocabTest() {
  const navigate = useNavigate()
  const [testWords, setTestWords] = useState<VocabTestWord[]>([])
  const [loading, setLoading] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selected, setSelected] = useState<string | null>(null)
  const [answered, setAnswered] = useState(false)
  const [score, setScore] = useState(0)
  const [finished, setFinished] = useState(false)

  const fetchTest = () => {
    setLoading(true)
    setCurrentIndex(0)
    setSelected(null)
    setAnswered(false)
    setScore(0)
    setFinished(false)
    vocabularyApi.getTestWords()
      .then((res) => setTestWords(res.data))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchTest() }, [])

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="green" />
      </Box>
    )
  }

  if (testWords.length === 0) {
    return (
      <Container size="sm" py={40}>
        <Paper shadow="sm" radius="md" p="xl" style={{ textAlign: 'center' }}>
          <Text c="dimmed">Not enough words available for a test.</Text>
          <Text size="xs" c="dimmed" mt={4}>Words need both a context sentence and a synonym to be testable.</Text>
          <Button mt={16} variant="outline" color="green" onClick={() => navigate('/vocabulary/progress')}>
            Back to Progress
          </Button>
        </Paper>
      </Container>
    )
  }

  // ── Results screen ──────────────────────────────────────────────────────────
  if (finished) {
    const total = testWords.length
    const pct = Math.round((score / total) * 100)
    const resultMantineColor = pct >= 70 ? 'green' : pct >= 40 ? 'yellow' : 'red'

    return (
      <Container size="sm" py={40}>
        <Paper shadow="md" radius="lg" p="xl" style={{ textAlign: 'center' }}>
          <Trophy size={52} color="var(--mantine-color-yellow-5)" style={{ marginBottom: 12 }} />
          <Text size="xl" fw={700} mb={4}>Test Complete!</Text>
          <Text size="xl" fw={800} c={resultMantineColor} style={{ fontSize: 64, lineHeight: 1 }}>
            {score}/{total}
          </Text>
          <Text size="sm" c="dimmed" mb={24}>{pct}% correct</Text>
          <Progress value={pct} color={resultMantineColor} size="lg" radius="xl" mb={32} />
          <Group justify="center" gap={16}>
            <Button
              leftSection={<ArrowLeft size={16} />}
              variant="outline"
              color="green"
              onClick={() => navigate('/vocabulary/progress')}
            >
              Back to Progress
            </Button>
            <Button leftSection={<RotateCcw size={16} />} color="green" onClick={fetchTest}>
              Retry
            </Button>
          </Group>
        </Paper>
      </Container>
    )
  }

  // ── Question screen ─────────────────────────────────────────────────────────
  const current = testWords[currentIndex]
  const total = testWords.length
  const isLastWord = currentIndex + 1 >= total
  const isCorrectAnswer = selected === current.correct_answer

  const handleCheck = () => {
    if (!selected || answered) return
    const correct = selected === current.correct_answer
    if (correct) setScore((s) => s + 1)
    setAnswered(true)
    vocabularyApi.submitTestResult(current.word_id, correct)
  }

  const handleNext = () => {
    if (isLastWord) {
      setFinished(true)
    } else {
      setCurrentIndex((i) => i + 1)
      setSelected(null)
      setAnswered(false)
    }
  }

  return (
    <Container size="sm" py={32}>
      {/* Progress header */}
      <Group justify="space-between" mb={8}>
        <Text size="sm" c="dimmed">Word {currentIndex + 1} of {total}</Text>
        <Badge color="green" size="lg" variant="light">{score} correct</Badge>
      </Group>
      <Progress value={((currentIndex + 1) / total) * 100} color="green" size="xs" radius="xl" mb={24} />

      <Paper shadow="sm" radius="md" p="xl">
        {/* Context sentence */}
        <Text size="lg" mb={24} style={{ lineHeight: 1.8 }}>
          {renderSentence(current.sentence, current.word)}
        </Text>

        {/* Question prompt */}
        <Text size="sm" fw={600} c="dimmed" mb={16}>
          Which word in the following options is similar to the above underlined word?
        </Text>

        {/* Choices */}
        <Stack gap={10}>
          {current.choices.map((choice) => {
            const isSelected = selected === choice
            const isCorrectChoice = choice === current.correct_answer
            const isWrongSelected = answered && isSelected && !isCorrectChoice

            let borderColor = 'var(--mantine-color-default-border)'
            let bg = 'transparent'
            if (!answered && isSelected) {
              borderColor = 'var(--mantine-color-green-6)'
              bg = 'var(--mantine-color-green-light)'
            } else if (answered && isCorrectChoice) {
              borderColor = 'var(--mantine-color-green-6)'
              bg = 'var(--mantine-color-green-light)'
            } else if (isWrongSelected) {
              borderColor = 'var(--mantine-color-red-6)'
              bg = 'var(--mantine-color-red-light)'
            }

            const dotActive = (isSelected && !answered) || (answered && isCorrectChoice)

            return (
              <Box
                key={choice}
                onClick={() => { if (!answered) setSelected(choice) }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderRadius: 8,
                  border: `1.5px solid ${borderColor}`,
                  background: bg,
                  cursor: answered ? 'default' : 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <Group gap={12}>
                  <Box style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${dotActive ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-gray-4)'}`,
                    background: dotActive ? 'var(--mantine-color-green-6)' : 'transparent',
                  }} />
                  <Text size="sm" fw={isSelected ? 600 : 400}>{choice}</Text>
                </Group>
                {answered && isCorrectChoice && <CheckCircle size={18} color="var(--mantine-color-green-6)" />}
                {isWrongSelected && <XCircle size={18} color="var(--mantine-color-red-6)" />}
              </Box>
            )
          })}
        </Stack>

        {/* Feedback */}
        {answered && (
          <Alert
            mt="md"
            color={isCorrectAnswer ? 'green' : 'red'}
            icon={isCorrectAnswer
              ? <CheckCircle size={18} />
              : <XCircle size={18} />
            }
          >
            {isCorrectAnswer
              ? 'Correct!'
              : <>Wrong! The correct answer is <strong>{current.correct_answer}</strong>.</>
            }
          </Alert>
        )}

        {/* Action buttons */}
        <Group mt={24} justify="flex-end">
          {!answered ? (
            <Button color="green" disabled={!selected} onClick={handleCheck}>
              Check Answer
            </Button>
          ) : (
            <Button color="green" onClick={handleNext}>
              {isLastWord ? 'See Results' : 'Next →'}
            </Button>
          )}
        </Group>
      </Paper>
    </Container>
  )
}
